import { useState } from "react";
import { championLeaderboard, initials } from "@/lib/scoring";
import { Crown, Trophy, FileText } from "lucide-react";
import PlayerCardModal from "@/screens/PlayerCardModal";
import ResultsSheetModal from "@/screens/ResultsSheetModal";

export default function ChampionTab({ event }) {
  const [openPlayerId, setOpenPlayerId] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const rows = championLeaderboard(event);
  const finalizedDays = (event.days || []).filter((d) => d.finalized).length;

  return (
    <div className="space-y-4 tga-fade">
      <div className="tga-card-dark">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 text-[color:var(--brass-bright)]"><Crown size={18}/> <span className="font-display text-2xl">Champion</span></div>
          {finalizedDays > 0 && (
            <button
              data-testid="champ-results-sheet-btn"
              className="brass-btn rounded-lg px-3 py-2 text-xs inline-flex items-center gap-1"
              onClick={() => setSheetOpen(true)}
            ><FileText size={14}/> Results sheet</button>
          )}
        </div>
        <p className="helptext">Cumulative Champion Points across {finalizedDays} finalized day{finalizedDays === 1 ? "" : "s"}.</p>
      </div>

      <div className="tga-card">
        {rows.length === 0 || finalizedDays === 0 ? (
          <p className="helptext text-[color:var(--text-dark)]/70">Finalize a day (Live tab) to award Champion Points.</p>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm font-mono-metrics">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[color:var(--brass-dark)] border-b border-[rgba(27,51,34,0.15)]">
                  <th className="py-1 px-2">#</th>
                  <th className="py-1 px-2">Player</th>
                  <th className="py-1 px-2 text-right">Days</th>
                  <th className="py-1 px-2 text-right" title="Fairways Hit">FH</th>
                  <th className="py-1 px-2 text-right" title="Greens in Regulation">GIR</th>
                  <th className="py-1 px-2 text-right" title="Total putts · avg per hole">Putts</th>
                  <th className="py-1 px-2 text-right">Pts</th>
                  <th className="py-1 px-2 text-right">CP</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const fhPct = r.fhAttempts ? Math.round((r.fhHit / r.fhAttempts) * 100) : null;
                  const girPct = r.holesPlayed ? Math.round((r.girHit / r.holesPlayed) * 100) : null;
                  const puttAvg = r.puttHoles ? (r.putts / r.puttHoles) : null;
                  return (
                    <tr key={r.player.id} className="tga-row border-b border-[rgba(27,51,34,0.08)] cursor-pointer" onClick={() => setOpenPlayerId(r.player.id)} data-testid={`champ-row-${r.player.id}`}>
                      <td className="py-2 px-2">{i + 1}</td>
                      <td className="py-2 px-2 flex items-center gap-2">
                        <div className="avatar" style={{ width: 26, height: 26, fontSize: 12 }}>{initials(r.player.name)}</div>
                        <span className="font-sans">{r.player.name}</span>
                        {i === 0 && r.championPoints > 0 && <Crown size={14} className="text-[color:var(--brass)]" />}
                        {r.dayWins > 0 && (
                          <span
                            data-testid={`champ-wins-${r.player.id}`}
                            className="inline-flex items-center gap-0.5 text-[10px] font-mono-metrics font-bold bg-[rgba(197,160,89,0.15)] text-[color:var(--brass-dark)] rounded-full px-1.5 py-0.5 border border-[rgba(197,160,89,0.4)]"
                            title={`${r.dayWins} best-round win${r.dayWins === 1 ? "" : "s"}`}
                          >
                            <Trophy size={10} /> {r.dayWins}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right">{r.daysPlayed}</td>
                      <td className="py-2 px-2 text-right">
                        {r.fhAttempts ? <>{r.fhHit}/{r.fhAttempts}<span className="text-[10px] opacity-60 ml-1">{fhPct}%</span></> : "—"}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {r.holesPlayed ? <>{r.girHit}/{r.holesPlayed}<span className="text-[10px] opacity-60 ml-1">{girPct}%</span></> : "—"}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {puttAvg !== null ? <>{r.putts}<span className="text-[10px] opacity-60 ml-1">{puttAvg.toFixed(2)}</span></> : "—"}
                      </td>
                      <td className="py-2 px-2 text-right">{r.points}</td>
                      <td className="py-2 px-2 text-right font-bold">{r.championPoints.toFixed(1)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PlayerCardModal
        open={!!openPlayerId}
        onClose={() => setOpenPlayerId(null)}
        event={event}
        playerId={openPlayerId}
      />

      <ResultsSheetModal
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        event={event}
        mode="championship"
      />
    </div>
  );
}
